import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment/moment";
import { _get, _post } from "../api/apiClient";
import { HttpStatusCode } from "axios";
import { CustomError } from "../api/CustomError";
import { CustomSuccess } from "../api/CustomSuccess";
import { API_STATUS } from "../rtk-query/features/sliceUtil";
import store from "../app/store";
import { setLoaderFalse, setLoaderTrue } from "./loaderSlice";
import { setForceLogout } from "../rtk-query/features/stripe/stripeSlice";
import { opportunitiesAnalysisSlice } from "../rtk-query/features/dashboard/opportunities/opportunitiesAnalysisSlice";
import { secondaryConsumerAnalysisSlice } from "../rtk-query/features/dashboard/secondaryConsumer/secondaryConsumerAnalysisSlice";
import {
  setIsFHSReset,
  setShowGaugeChart,
  setSkipFHSAPICall,
} from "../rtk-query/features/dashboard/financialHealthScore/financialHealthScoreAnalysisSlice";
import { missedRevenueAnalysisSlice } from "../rtk-query/features/dashboard/missedRevenue/missedRevenueAnalysisSlice";
import { bondScoreAnalysisSlice } from "../rtk-query/features/dashboard/bondScore/bondScoreAnalysisSlice";
import { topCompetitorsAnalysisSlice } from "../rtk-query/features/dashboard/topCompetitors/topCompetitorsAnalysisSlice";
import { insightsAndRecommendationSlice } from "../rtk-query/features/insightsAndRecommendation/insightsAndRecommendationSlice";
import { bondScoreTrendSlice } from "../rtk-query/features/dashboard/trends/bondScoreTrendSlice";
import { ghostConsumersTrendSlice } from "../rtk-query/features/dashboard/trends/ghostConsumersTrendSlice";
import { missingRevenueTrendSlice } from "../rtk-query/features/dashboard/trends/missingRevenueTrendSlice";
import { opportunitiesTrendSlice } from "../rtk-query/features/dashboard/trends/opportunitiesTrendSlice";
import { secondaryConsumersTrendSlice } from "../rtk-query/features/dashboard/trends/secondaryConsumersTrendSlice";
import { resetContactUs } from "./contactUsSlice";
import { initialFilterDataSlice } from "./initialFilterDataSlice";
import { clearPrivileges } from "./privilegesSlice";
import { resetNotifications } from "../rtk-query/features/usernotification/notificationSlice";

const initialState = {
  userData: sessionStorage.getItem("userData")
    ? JSON.parse(sessionStorage.getItem("userData"))
    : {},
  isLogin: sessionStorage.getItem("isLogin") ? true : false,
  expiresIn: sessionStorage.getItem("expiresIn") ?? null,
  expirationTime: sessionStorage.getItem("expirationTime") ?? null,
  loggedInTime: sessionStorage.getItem("loggedInTime") ?? null,
  userContactedSales: false,
  isLoading: API_STATUS.STATUS_IDLE,
};

const _refreshToken = () => {
  const state = store.getState();
  return state?.authReducer?.userData?.refresh_Token;
};

export const ResetReduxState = createAsyncThunk({}, async (__, thunkAPI) => {
  try {
    thunkAPI.dispatch(clearPrivileges());
    thunkAPI.dispatch(setSkipFHSAPICall(false));
    thunkAPI.dispatch(setIsFHSReset(true));
    thunkAPI.dispatch(setShowGaugeChart(false));
    thunkAPI.dispatch(resetContactUs());
    thunkAPI.dispatch(resetNotifications());
    thunkAPI.dispatch(resetLockedCampaignSlice());
    thunkAPI.dispatch(opportunitiesAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(secondaryConsumerAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(missedRevenueAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(bondScoreAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(topCompetitorsAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(insightsAndRecommendationSlice.util.resetApiState());
    thunkAPI.dispatch(bondScoreTrendSlice.util.resetApiState());
    thunkAPI.dispatch(ghostConsumersTrendSlice.util.resetApiState());
    thunkAPI.dispatch(missingRevenueTrendSlice.util.resetApiState());
    thunkAPI.dispatch(opportunitiesTrendSlice.util.resetApiState());
    thunkAPI.dispatch(secondaryConsumersTrendSlice.util.resetApiState());
    thunkAPI.dispatch(initialFilterDataSlice.util.resetApiState());

    return true;
  } catch (err) {
    if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
    return thunkAPI.rejectWithValue(err);
  }
});

export const LogOut = createAsyncThunk("/auth/logout", async (_, thunkAPI) => {
  try {
    thunkAPI.dispatch(setLoaderTrue());
    await thunkAPI.dispatch(ResetReduxState());
    const config = {
      successMessage: "Login Success!",
      errorMessage: "Login Error!",
      disableToast: true,
    };
    const response = await _get(
      `/auth/logout?refresh_token=${_refreshToken()}`,
    );
    if (response?.statusCode !== HttpStatusCode.Ok) {
      throw CustomError(response, config);
    }
    CustomSuccess(response, {
      ...config,
    });
    thunkAPI.dispatch(setForceLogout(false));
    thunkAPI.dispatch(setLoaderFalse());
    return null;
  } catch (err) {
    if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
    return thunkAPI.rejectWithValue(err);
  }
});

export const LoginThunk = createAsyncThunk(
  "auth/Login",
  async (data, thunkAPI) => {
    try {
      await thunkAPI.dispatch(ResetReduxState());
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Login Success!",
        errorMessage: "Login Error!",
      };
      const response = await _post(`/auth/login`, data);

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const _response = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });
      thunkAPI.dispatch(setLoaderFalse());
      return _response;
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const authSlice = createSlice({
  name: "authSlice",
  initialState,
  reducers: {
    login: (state, action) => {
      state.userData = action.payload;
      state.isLogin = true;
      state.token = action.payload?.token;
      state.expiresIn = action.payload?.expiresIn;
      state.expirationTime = moment()
        .add(action.payload?.expiresIn, "seconds")
        .format();
      state.loggedInTime = moment().format();
      state.createdOn = action.payload?.createdOn;
      sessionStorage.setItem("userData", JSON.stringify(state.userData));
      sessionStorage.setItem("isLogin", state.isLogin);
      sessionStorage.setItem("expiresIn", state.expiresIn);
      sessionStorage.setItem("expirationTime", state.expirationTime);
      sessionStorage.setItem("loggedInTime", state.loggedInTime);
    },
    setFirstName: (state, action) => {
      state.userData.firstName = action.payload;
      sessionStorage.setItem("userData", JSON.stringify(state.userData));
    },
    setLastName: (state, action) => {
      state.userData.lastName = action.payload;
      sessionStorage.setItem("userData", JSON.stringify(state.userData));
    },
    logOut: (state) => {
      state.userData = {};
      state.isLogin = false;
    },
    setUserContactedSales: (state, action) => {
      state.userContactedSales = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(LogOut.pending, (state, action) => {})
      .addCase(LogOut.fulfilled, (state, action) => {
        sessionStorage.clear();
        state.userData = {};
        state.isLogin = false;
        state.token = null;
      })
      .addCase(LogOut.rejected, (state, action) => {
        sessionStorage.clear();
        state.userData = {};
        state.isLogin = false;
        state.token = null;
      });
  },
});

// Action creators are generated for each case reducer function
export const {
  login,
  logOut,
  setFirstName,
  setLastName,
  setUserContactedSales,
} = authSlice.actions;

export default authSlice.reducer;
